//External libs
import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Subscription } from 'rxjs';

//Local libs
import Service from 'src/app/common/service';
import FeedRateModel from './feedRate.model';

/**
 * @service FeedRateService
 * @description Service for managing feed rate projections and comparisons.
 * Provides HTTP methods to retrieve projected feed rates based on culture data,
 * allowing comparison between actual and projected feed consumption.
 * Extends base Service class with generic type FeedRateModel.
 *
 * @feature 1. Calculate and retrieve projected feed rates
 * @feature 2. Compare daily feed consumption (Actual vs Projected)
 * @feature 3. Cancel ongoing HTTP requests to prevent memory leaks
 *
 * @architecture
 * Extends base Service<FeedRateModel> providing REST API integration.
 * Uses HttpClient for HTTP operations and manages subscriptions for cleanup.
 *
 * @service-endpoint /blubay/feedrate/
 * @service-provider root (singleton, application-wide)
 *
 * @author AquaCloud Development Team
 * @version 1.0.0
 */
@Injectable({
  providedIn: 'root'
})
export class FeedRateService extends Service<FeedRateModel> {

  /**
   * @type {Array<Subscription>}
   * @description Array tracking all ongoing HTTP request subscriptions
   * @default []
   * @side-effect Used to store subscription references for cancellation via cancelOnGoingHttpRequests()
   */
  public ongoingRequests: Subscription[] = [];

  /**
   * @method constructor
   * @description Service constructor with dependency injection of HttpClient
   *
   * Initializes service by calling parent Service constructor with baseUrl
   * configuration and service endpoint path.
   *
   * @param {HttpClient} httpClient - @service Angular HTTP client for making HTTP requests
   * @returns {void}
   */
  constructor(public httpClient: HttpClient) {
    super(httpClient, '/blubay/feedrate/');
  }

  /**
   * @method getProjectedFeedRate
   * @async
   * @description Fetch projected feed rate data for daily comparison chart
   *
   * Makes HTTP POST request to backend endpoint /blubay/feedrate/dailyFeedComparisonChart.
   * Retrieves data to compare actual feed given vs projected feed requirements based on
   * culture, pond, and species type.
   *
   * @param {string} cultureId - ID of the pond culture
   * @param {string} pondId - ID of the pond
   * @param {string} speciesTypeId - ID of the species type
   * @returns {Promise<Array<FeedRateModel>>} Promise resolving to array of FeedRateModel objects
   * @side-effect
   * - Creates HTTP POST request and stores subscription reference in ongoingRequests
   * - Subscription can be cancelled via cancelOnGoingHttpRequests()
   *
   * @throws Rejects promise with HttpErrorResponse if request fails
   * @example
   * const feedRates = await feedRateService.getProjectedFeedRate('culture123', 'pond456', 'species789');
   * console.log(feedRates); // Array of FeedRateModel objects
   */
  public getProjectedFeedRate(cultureId: string, pondId: string, speciesTypeId: string) {
    return new Promise<Array<FeedRateModel>>((resolve, reject) => {
      const request = this.httpClient.post<Array<FeedRateModel>>(`${this.baseUrl}${this.serviceUrl}dailyFeedComparisonChart`, {
        cultureID: cultureId,
        pondID: pondId,
        speciesTypeID: speciesTypeId
      }, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  //To Cancel the onGoing Http Requests
  /**
   * @method cancelOnGoingHttpRequests
   * @description Cancel all ongoing/pending HTTP requests to prevent memory leaks
   *
   * Iterates through ongoingRequests array and unsubscribes from each active
   * subscription. Clears the requests array after unsubscribing from all.
   *
   * @execution-flow
   * 1. Iterate through each subscription in ongoingRequests array
   * 2. Check if subscription has unsubscribe function
   * 3. Call unsubscribe() on each active subscription
   * 4. Clear ongoingRequests array
   *
   * @returns {void}
   * @side-effect
   * - Aborts all pending HTTP requests
   * - Clears ongoingRequests array
   *
   * @usage Called during component ngOnDestroy to cleanup subscriptions
   * @example
   * ngOnDestroy() {
   *   this.feedRateService.cancelOnGoingHttpRequests();
   * }
   */
  public cancelOnGoingHttpRequests(): void {
    this.ongoingRequests.forEach((request) => {
      if (request && typeof request.unsubscribe === 'function') {
        request.unsubscribe();
      }
    });
    this.ongoingRequests = [];
  }
}
